// BiomeManager.js
// This class manages biome assignment based on height and moisture maps

import NoiseGenerator from './NoiseGenerator.js';

/**
 * Manages biome assignment based on height and moisture data
 */
class BiomeManager {
  /**
   * Define all biome types with their properties
   */
  static BIOMES = {
    DEEP_WATER: {
      id: 'deep_water',
      name: 'Deep Water',
      color: [0, 20, 120]
    },
    SHALLOW_WATER: {
      id: 'shallow_water',
      name: 'Shallow Water',
      color: [30, 120, 190]
    },
    BEACH: {
      id: 'beach',
      name: 'Beach',
      color: [240, 220, 150]
    },
    GRASSLAND: {
      id: 'grassland',
      name: 'Grassland',
      color: [110, 200, 95]
    },
    FOREST: {
      id: 'forest',
      name: 'Forest',
      color: [40, 140, 40]
    },
    ROCKY_TERRAIN: {
      id: 'rocky_terrain',
      name: 'Rocky Terrain',
      color: [120, 100, 80]
    },
    MOUNTAINS: {
      id: 'mountains',
      name: 'Mountains',
      color: [140, 120, 100]
    },
    SNOW: {
      id: 'snow',
      name: 'Snow',
      color: [230, 230, 240]
    }
  };

  /**
   * Generate a moisture map using simplex noise
   * @param {number} width - Width of the map
   * @param {number} height - Height of the map
   * @param {Object} options - Options for moisture generation
   * @param {number} options.noiseScale - Scale of the noise (default: 200)
   * @param {number} options.octaves - Number of octaves for noise (default: 4)
   * @param {number} options.persistence - Persistence for noise (default: 0.5)
   * @param {number} options.lacunarity - Lacunarity for noise (default: 2)
   * @param {string|number} options.seed - Seed for noise generation (default: 'moisture')
   * @returns {number[][]} 2D array of moisture values between 0 and 1
   */
  static generateMoistureMap(width, height, options = {}) {
    const defaultOptions = {
      noiseScale: 130,
      octaves: 6,
      persistence: 0.5,
      lacunarity: 2.5,
      seed: 'moisture'
    };
    
    const config = { ...defaultOptions, ...options };
    
    return NoiseGenerator.generateSimplexNoise(
      width,
      height,
      config.noiseScale,
      config.octaves,
      config.persistence,
      config.lacunarity,
      config.seed
    );
  }

  /**
   * Generate a biome map based on height and moisture maps
   * @param {number[][]} heightMap - 2D array of height values (0-1)
   * @param {number[][]} moistureMap - 2D array of moisture values (0-1)
   * @param {number[][]} islandMask - 2D array indicating land/water (optional)
   * @returns {Object[][]} 2D array of biome objects with id, name, and color properties
   */
  static generateBiomeMap(heightMap, moistureMap, islandMask = null) {
    const width = heightMap[0].length;
    const height = heightMap.length;
    
    // Initialize biome map with deep water
    const biomeMap = Array(height)
      .fill()
      .map(() => Array(width).fill(this.BIOMES.DEEP_WATER));
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const heightValue = heightMap[y][x];
        const moistureValue = moistureMap[y][x];
        
        // If island mask is provided, use it to determine terrain types
        if (islandMask) {
          const maskValue = islandMask[y][x];
          
          // Use threshold-based checks instead of exact equality
          if (maskValue < 0.1) {
            // Deep water (value close to 0)
            biomeMap[y][x] = this.BIOMES.DEEP_WATER;
            continue;
          } else if (maskValue > 0.2 && maskValue < 0.4) {
            // Shallow water (value close to 0.3)
            biomeMap[y][x] = this.BIOMES.SHALLOW_WATER;
            continue;
          } else if (maskValue > 0.6 && maskValue < 0.8) {
            // Beach (value close to 0.7)
            biomeMap[y][x] = this.BIOMES.BEACH;
            continue;
          } else if (maskValue > 0.9) {
            // Land - let the height and moisture determine the specific biome
            if (heightValue < 0.7) {
              // Lowland biomes
              if (moistureValue < 0.4) {
                biomeMap[y][x] = this.BIOMES.GRASSLAND;
              } else {
                biomeMap[y][x] = this.BIOMES.FOREST;
              }
            } else if (heightValue < 0.85) {
              biomeMap[y][x] = this.BIOMES.ROCKY_TERRAIN;
            } else if (heightValue < 0.95) {
              biomeMap[y][x] = this.BIOMES.MOUNTAINS;
            } else {
              biomeMap[y][x] = this.BIOMES.SNOW;
            }
            continue;
          }
        }
        
        // If no mask is provided or the mask value doesn't match any terrain type,
        // determine biome based on height value
        if (heightValue < 0.1) {
          biomeMap[y][x] = this.BIOMES.DEEP_WATER;
        } else if (heightValue < 0.2) {
          biomeMap[y][x] = this.BIOMES.SHALLOW_WATER;
        } else if (heightValue < 0.3) {
          biomeMap[y][x] = this.BIOMES.BEACH;
        } else if (heightValue < 0.7) {
          // Land biomes depend on both height and moisture
          if (moistureValue < 0.4) {
            biomeMap[y][x] = this.BIOMES.GRASSLAND;
          } else {
            biomeMap[y][x] = this.BIOMES.FOREST;
          }
        } else if (heightValue < 0.85) {
          biomeMap[y][x] = this.BIOMES.ROCKY_TERRAIN;
        } else if (heightValue < 0.95) {
          biomeMap[y][x] = this.BIOMES.MOUNTAINS;
        } else {
          biomeMap[y][x] = this.BIOMES.SNOW;
        }
      }
    }
    
    return biomeMap;
  }

  /**
   * Generate a preview of the biome map as a data URL
   * @param {Object[][]} biomeMap - 2D array of biome objects
   * @returns {string} Data URL of the generated image
   */
  static generateBiomeMapPreview(biomeMap) {
    const width = biomeMap[0].length;
    const height = biomeMap.length;
    
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(width, height);
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const biome = biomeMap[y][x];
        const index = (y * width + x) * 4;
        
        imageData.data[index] = biome.color[0];
        imageData.data[index + 1] = biome.color[1];
        imageData.data[index + 2] = biome.color[2];
        imageData.data[index + 3] = 255;
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  }

  /**
   * Get the biome at a specific point in the biome map
   * @param {Object[][]} biomeMap - 2D array of biome objects
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @returns {Object} Biome object at the given coordinates
   */
  static getBiomeAt(biomeMap, x, y) {
    if (x < 0 || x >= biomeMap[0].length || y < 0 || y >= biomeMap.length) {
      return this.BIOMES.DEEP_WATER;
    }
    return biomeMap[y][x];
  }
}

export default BiomeManager; 